<template>
  <loading-dialog :loading="loadingPage">
    <base-page-layout ref="basePage" :title="pageTitle">
      <stepper :steppers="steppers" :step="4" />

      <status-icon :status="campaign.status" />

      <h2 class="page-subtitle mt-2">Mensagem</h2>

      <p class="page-description mt-2">
        Essa parte é essencial para que você possa criar mensagens personalizadas para cada um dos seus consumidores. Usando o produto e o preço como variáveis.
      </p>

      <v-container class="pa-0" fluid>
        <v-row>
          <v-col cols="12">
            <v-card>
              <toggle-channel
                v-model="campaign.shotConfiguration.sms.appChannelOptions"
                :disabled="!hasPermission('sms') || isMariaCampaignActivated || isMariaCampaignFinished"
                label="Usar o canal SMS"
              />
              <sms-channel
                v-if="hasPermission('sms') && campaign.shotConfiguration.sms.appChannelOptions.toggleAppExpanded"
                :disabled="!campaign.shotConfiguration.sms.appChannelOptions.toggleAppActivated || isMariaCampaignActivated || isMariaCampaignFinished"
                :text.sync="campaign.shotConfiguration.sms.text"
                :credits.sync="campaign.shotConfiguration.sms.credits"
                :campaign="campaign"
                :is-maria-campaign-activated="isMariaCampaignActivated"
                :is-maria-campaign-finished="isMariaCampaignFinished"
                :offer-type="campaign.offerType"
                :validations="$v"
              />
              <forbidden-channel v-else-if="!hasPermission('sms') && campaign.shotConfiguration.sms.appChannelOptions.toggleAppExpanded" />
            </v-card>
          </v-col>

          <v-col cols="12">
            <v-card>
              <toggle-channel
                v-model="campaign.shotConfiguration.whatsapp.appChannelOptions"
                :disabled="!hasPermission('whatsapp') || isMariaCampaignActivated || isMariaCampaignFinished"
                label="Usar o canal Whatsapp"
              />
              <whatsapp-channel
                v-if="hasPermission('whatsapp') && campaign.shotConfiguration.whatsapp.appChannelOptions.toggleAppExpanded"
                :campaign.sync="campaign"
                :disabled="!campaign.shotConfiguration.whatsapp.appChannelOptions.toggleAppActivated || isMariaCampaignActivated || isMariaCampaignFinished"
                :is-maria-campaign-finished="isMariaCampaignFinished"
                :validations="$v"
              />
              <forbidden-channel v-else-if="!hasPermission('whatsapp') && campaign.shotConfiguration.whatsapp.appChannelOptions.toggleAppExpanded" />
            </v-card>
          </v-col>

          <v-col cols="12">
            <v-card>
              <toggle-channel
                v-model="campaign.shotConfiguration.push.appChannelOptions"
                :disabled="!hasPermission('push') || isMariaCampaignActivated || isMariaCampaignFinished"
                label="Usar o canal Push"
              />
              <push-configuration
                v-if="hasPermission('push') && campaign.shotConfiguration.push.appChannelOptions.toggleAppExpanded"
                class="px-6 mt-n4"
                :disabled="!campaign.shotConfiguration.push.appChannelOptions.toggleAppActivated || isMariaCampaignActivated || isMariaCampaignFinished"
                :is-maria-campaign-activated="isMariaCampaignActivated"
                :is-maria-campaign-finished="isMariaCampaignFinished"
                :show-title="false"
                :push-configuration.sync="campaign.shotConfiguration.push"
                :has-title-error="getSafe(() => $v.campaign.shotConfiguration.push.title.$error)"
                :has-text-error="getSafe(() => $v.campaign.shotConfiguration.push.text.$error)"
                :has-message-title-error="getSafe(() => $v.campaign.shotConfiguration.push.inner_message_title.$error)"
                :has-message-text-error="getSafe(() => $v.campaign.shotConfiguration.push.inner_message_text.$error)"
                :has-image-error="getSafe(() => $v.campaign.shotConfiguration.push.image_url.$error)"
                :show-image.sync="showImage"
              />
              <clients-range-slider
                v-if="hasPermission('push') && campaign.shotConfiguration.push.appChannelOptions.toggleAppExpanded"
                class="mb-7"
                channel="push"
                :campaign-version="campaignVersion"
                :segments="campaign.segments"
                :version="campaign.version"
              />
              <redirection
                v-if="hasPermission('push') && campaign.shotConfiguration.push.appChannelOptions.toggleAppExpanded"
                :tab.sync="tab"
                :redirect-external.sync="redirectExternal"
                :redirect-internal.sync="redirectInternal"
                :selected-flyer.sync="selectedFlyer"
                :validations="$v"
                :disabled="!campaign.shotConfiguration.push.appChannelOptions.toggleAppActivated || isMariaCampaignActivated || isMariaCampaignFinished"
                is-maria-campaign
              />
              <credit-entry
                v-if="hasPermission('push') && campaign.shotConfiguration.push.appChannelOptions.toggleAppExpanded"
                title="Quantidade de disparos push disponíveis"
                :credits.sync="campaign.shotConfiguration.push.credits"
                :disabled="!campaign.shotConfiguration.push.appChannelOptions.toggleAppActivated || isMariaCampaignActivated"
                :is-maria-campaign-activated="isMariaCampaignActivated"
                :is-maria-campaign-finished="isMariaCampaignFinished"
                channel-type="push"
                :validations="
                  getSafe(() => $v.campaign.shotConfiguration.push.credits.$error) ? 'Defina a quantidade de créditos disponíveis nesta campanha.' : ''
                "
              />
              <forbidden-channel v-else-if="!hasPermission('push') && campaign.shotConfiguration.push.appChannelOptions.toggleAppExpanded" />
            </v-card>
          </v-col>

          <v-col cols="12" class="email-marketing">
            <v-card>
              <toggle-channel
                v-model="campaign.shotConfiguration.email.appChannelOptions"
                :disabled="!hasPermission('email_v2') || isMariaCampaignActivated || isMariaCampaignFinished"
                label="Usar o canal E-mail"
              />
              <email-channel
                v-if="hasPermission('email_v2') && campaign.shotConfiguration.email.appChannelOptions.toggleAppExpanded"
                ref="emailChannel"
                :campaign="campaign"
                :is-maria-campaign-activated="isMariaCampaignActivated"
                :is-maria-campaign-finished="isMariaCampaignFinished"
                :disabled="!campaign.shotConfiguration.email.appChannelOptions.toggleAppActivated || isMariaCampaignActivated || isMariaCampaignFinished"
                :text.sync="campaign.shotConfiguration.email.text"
                :credits.sync="campaign.shotConfiguration.email.credits"
                :validations="$v"
                :prices-validation="pricesValidation"
                @selectEmailType="selectEmailType"
                @validEmailChannel="validatePreviewInfos"
              />
              <forbidden-channel v-else-if="!hasPermission('email_v2') && campaign.shotConfiguration.email.appChannelOptions.toggleAppExpanded" />
            </v-card>
          </v-col>
        </v-row>

        <mf-action-buttons
          v-if="campaign.status"
          :primary-button="primaryButton"
          :secondary-button="secondaryButton"
          :extra-button="extraButton"
          :cancel-button="cancelButton"
        />
      </v-container>
    </base-page-layout>
  </loading-dialog>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex'
import { minLength, maxLength, required, requiredIf } from 'vuelidate/lib/validators'

import BasePageLayout from '@/components/BasePageLayout.vue'
import { characterVariables } from '@/mixins/character-variables'
import { notOnlySpace, isUrl, isPositive } from '@/helpers/validators.js'
import SmsChannel from '@/modules/maria/campanha-da-maria/views/components/SmsChannel.vue'
import CreditEntry from '@/modules/maria/campanha-da-maria/views/components/CreditEntry.vue'
import EmailChannel from '@/modules/maria/campanha-da-maria/views/components/EmailChannel.vue'
import ToggleChannel from '@/modules/maria/campanha-da-maria/views/components/ToggleChannel.vue'
import { mariaCampaignActions } from '@/modules/maria/campanha-da-maria/mixins/maria-campaign-actions'
import ForbiddenChannel from '@/modules/maria/campanha-da-maria/views/components/ForbiddenChannel.vue'
import ClientsRangeSlider from '@/modules/maria/campanha-da-maria/views/components/ClientsRangeSlider.vue'
import PushConfiguration from '@/modules/campanhas/campanhas-aplicativo/views/components/PushConfiguration.vue'
import WhatsappChannel from '@/modules/maria/campanha-da-maria/views/components/WhatsappChannel.vue'
import Redirection from '@/modules/campanhas/campanhas-aplicativo/views/components/Redirection.vue'

export default {
  name: 'ShotConfiguration',

  components: {
    BasePageLayout,
    ClientsRangeSlider,
    ToggleChannel,
    PushConfiguration,
    SmsChannel,
    EmailChannel,
    CreditEntry,
    ForbiddenChannel,
    WhatsappChannel,
    Redirection,
  },

  mixins: [mariaCampaignActions, characterVariables],

  data: () => ({
    steppers: [
      {
        step: 1,
        complete: true,
        stepName: 'Escolher produtos',
        pathName: '',
      },
      {
        step: 2,
        complete: true,
        stepName: 'Detalhes dos produtos',
        pathName: '',
      },
      {
        step: 3,
        complete: true,
        stepName: 'Orçamento',
        pathName: '',
      },
      {
        step: 4,
        complete: false,
        stepName: 'Canais',
        pathName: '',
      },
      {
        step: 5,
        complete: false,
        stepName: 'Revisão',
        pathName: '',
      },
    ],
    tryingToSaveAsDraft: false,
    defaultMsg: false,
    errorShotQuantity: true,
    selectedEmailType: 'SIMPLE',
    showImage: true,

    tab: 'offers',
    redirectExternal: '',
    redirectInternal: 'home',
    selectedFlyer: '',
  }),

  computed: {
    ...mapGetters(['mariaCampaign']),

    campaignVersion() {
      return this.campaign.version === 2 || !this.campaign._id ? 'V2' : 'V1'
    },

    pageTitle() {
      return 'Nova campanha da mar.ia'
    },

    maxSubjectLength() {
      return this.lengthCounter(this.campaign.shotConfiguration.email.message.subject, 50)
    },

    maxTextLength() {
      return this.lengthCounter(this.campaign.shotConfiguration.email.message.text, 2000)
    },

    maxButtonTextLength() {
      return this.lengthCounter(this.campaign.shotConfiguration.email.message.click_to_call_button_text, 20)
    },

    maxFooterLength() {
      return this.lengthCounter(this.campaign.shotConfiguration.email.message.footer, 250)
    },

    maxWhatsappLength() {
      return this.lengthCounter(this.campaign.shotConfiguration.whatsapp.message.text, 2000)
    },

    maxPushTitleLength() {
      return this.lengthCounter(this.campaign.shotConfiguration.push.title, 48)
    },

    maxPushTextLength() {
      return this.lengthCounter(this.campaign.shotConfiguration.push.text, 100)
    },

    maxPushInnerTitleLength() {
      return this.lengthCounter(this.campaign.shotConfiguration.push.inner_message_title, 50)
    },

    maxPushInnerTextLength() {
      return this.lengthCounter(this.campaign.shotConfiguration.push.inner_message_text, 500)
    },

    primaryButton() {
      return {
        text: 'Continuar',
        action: this.tryingToSaveCampaign,
        isVisible: true,
        isDisabled: this.loadingSaveDraft,
        isLoading: this.loadingSaveActive,
      }
    },

    secondaryButton() {
      return this.isMariaCampaignFinished
        ? {}
        : {
            text: 'Salvar e sair',
            action: this.saveDraft,
            isVisible: !this.isCampaignActivated,
            isDisabled: false,
            isLoading: this.loadingSaveDraft,
          }
    },

    extraButton() {
      return {
        text: 'Voltar',
        action: this.goBack,
        isVisible: true,
        isDisabled: false,
      }
    },

    cancelButton() {
      return {
        text: this.isMariaCampaignFinished ? 'Fechar' : 'Cancelar',
        action: this.redirectCampaigns,
        isVisible: true,
        isDisabled: false,
      }
    },
    pricesValidation() {
      let isValidPrices = false
      if (!this.hasPrice() && this.hasSubvariable()) {
        isValidPrices = true
      }
      return isValidPrices
    },

    getAction() {
      let action = {}

      switch (this.tab) {
        case 'external':
          action = {
            type: 'EXTERNAL',
            link: this.redirectExternal,
            reference_id: null,
          }
          break
        case 'internal':
          action = {
            type: 'INTERNAL',
            link: this.redirectInternal === 'home' ? '' : this.redirectInternal,
            reference_id: this.redirectInternal === 'FlyersScreen' ? this.selectedFlyer : null,
          }
          break
        case 'offer':
          action = {
            type: 'INTERNAL',
            link: 'SalesOmniCampaignScreen',
            reference_id: null,
          }
          break
      }

      return action
    },
  },

  watch: {
    'campaign.shotConfiguration.whatsapp': {
      deep: true,
      handler() {
        this.campaign.shotConfiguration.whatsapp.message.type = this.getWhatsappType
      },
    },
  },

  created() {
    window.scrollTo(0, 0)
    if (!this.mariaCampaign.title) return this.goTomariaCampaignIndexTable()
    this.campaign = { ...this.campaign, ...this.mariaCampaign }
    if (this.campaign?._id && !this.campaign?.push?.message?.image_url) this.showImage = false
    if (this.campaign.shotConfiguration?.push?.appChannelOptions?.toggleAppActivated) {
      const { action } = this.campaign.shotConfiguration?.push
      if (action?.type) {
        const { link, type, reference_id } = action
        if (type === 'INTERNAL' && link !== 'SalesOmniCampaignScreen') {
          this.tab = 'internal'
          this.redirectInternal = link || 'home'
          this.selectedFlyer = link === 'FlyersScreen' ? reference_id : null
        } else if (type === 'INTERNAL' && link === 'SalesOmniCampaignScreen') {
          this.tab = 'offer'
        } else {
          this.tab = 'external'
          this.redirectExternal = link
        }
      }
    } else {
      this.tab = 'offer'
    }
    if (this.campaign.shotConfiguration?.push?.image_url) this.showImage = true
    if (this.campaign?.channels?.email?.message?.email_configuration_type) this.selectEmailType(this.campaign.channels.email.message.email_configuration_type)
  },

  methods: {
    ...mapMutations(['SET_MARIA_CAMPAIGN']),

    selectEmailType(type) {
      this.selectedEmailType = type
    },

    hasPermission(channel) {
      if (channel === 'sms') return true
      return this.$getAllowedModules()[channel]
    },
    hasPrice() {
      if (!this.campaign.offer_sales.length) return true
      return this.campaign.offer_sales.every((product) => product.price_1 && product.price_2)
    },
    smsSubvariable() {
      let hasSms = false
      const messageWithVariable = this.messageWithVariable(this.campaign.shotConfiguration.sms.text, true)
      if (this.campaign.shotConfiguration.sms.appChannelOptions.toggleAppActivated) {
        hasSms = messageWithVariable.includes('{preço 1}') || messageWithVariable.includes('{preço 2}')
      }
      return hasSms
    },
    pushSubvariable() {
      let hasPush = false
      const messageTitleWithVariable = this.messageWithVariable(this.campaign.shotConfiguration.push.title, true)
      const messageTextWithVariable = this.messageWithVariable(this.campaign.shotConfiguration.push.text, true)
      const messageInnerTextWithVariable = this.messageWithVariable(this.campaign.shotConfiguration.push.inner_message_text, true)
      const messageInnerTitleWithVariable = this.messageWithVariable(this.campaign.shotConfiguration.push.inner_message_title, true)
      if (this.campaign.shotConfiguration.push.appChannelOptions.toggleAppActivated) {
        hasPush =
          messageTitleWithVariable.includes('{preço 1}') ||
          messageTitleWithVariable.includes('{preço 2}') ||
          messageTextWithVariable.includes('{preço 1}') ||
          messageTextWithVariable.includes('{preço 2}') ||
          messageInnerTextWithVariable.includes('{preço 1}') ||
          messageInnerTextWithVariable.includes('{preço 2}') ||
          messageInnerTitleWithVariable.includes('{preço 1}') ||
          messageInnerTitleWithVariable.includes('{preço 2}')
      }
      return hasPush
    },
    emailSubvariable() {
      let hasEmail = false
      const messageWithVariable = this.messageWithVariable(this.campaign.shotConfiguration.email.message.text, true)
      const messageSubjectWithVariable = this.messageWithVariable(this.campaign.shotConfiguration.email.message.subject, true)
      const messageFooterWithVariable = this.messageWithVariable(this.campaign.shotConfiguration.email.message.footer, true)
      if (this.campaign.shotConfiguration.email.appChannelOptions.toggleAppActivated) {
        hasEmail =
          messageWithVariable.includes('{preço 1}') ||
          messageWithVariable.includes('{preço 2}') ||
          messageSubjectWithVariable.includes('{preço 1}') ||
          messageSubjectWithVariable.includes('{preço 2}') ||
          messageFooterWithVariable.includes('{preço 1}') ||
          messageFooterWithVariable.includes('{preço 2}')
      }
      return hasEmail
    },
    whatsAppSubvariable() {
      let hasWhats = false
      const messageWithVariable = this.messageWithVariable(this.campaign.shotConfiguration.whatsapp.message.text, true)
      if (this.campaign.shotConfiguration.whatsapp.appChannelOptions.toggleAppActivated) {
        hasWhats = messageWithVariable.includes('{preço 1}') || messageWithVariable.includes('{preço 2}')
      }
      return hasWhats
    },
    hasSubvariable() {
      const hasSubvariable = this.smsSubvariable() || this.pushSubvariable() || this.emailSubvariable() || this.whatsAppSubvariable()
      return hasSubvariable
    },
    async validateInfos() {
      if (this.campaign?.shotConfiguration?.email?.appChannelOptions?.toggleAppActivated && this.selectedEmailType === 'BUILDER') {
        await this.$refs.emailChannel.loadCampaignEmailConfiguration()
      }
      let isValid = true
      this.allInputsRequired = true
      this.tryingToSaveAsDraft = false
      this.$v.$reset()
      this.$v.$touch()
      isValid = !this.$v.$error
      if (!isValid) {
        if (!this.hasChannelActivated()) {
          isValid = false
          this.$snackbar({
            message: 'Selecione pelo menos um canal de disparo.',
            snackbarColor: 'error',
          })
          return isValid
        }
        this.$snackbar({
          message: 'Preencha todos os campos corretamente.',
          snackbarColor: 'error',
        })
        this.$nextTick(() => {
          this.$vuetify.goTo(document.querySelector('.error--text'), { offset: 200 })
        })
      } else if (!this.hasPrice() && this.hasSubvariable()) {
        isValid = false
        this.$snackbar({
          message: this.defaultMsg
            ? 'Para utilizar as subvariáveis de preço você precisa configurar seus respectivos valores no passo 2. Por favor volte e preencha os campos deixados em branco ou remova os preços das suas mensagens.'
            : 'Para utilizar as subvariáveis de preço na preview de e-mail você precisa configurar seus respectivos valores no passo 2. Por favor volte e preencha os campos deixados em branco ou remova os preços das suas mensagens.',
          snackbarColor: 'error',
          timeout: 60000,
        })
        return isValid
      } else {
        if (!this.hasChannelActivated()) {
          isValid = false
          this.$snackbar({
            message: 'Selecione pelo menos um canal de disparo.',
            snackbarColor: 'error',
          })
          return isValid
        }
      }
      this.confirmCampaign = true
      return isValid
    },

    hasChannelActivated() {
      return [
        this.campaign.shotConfiguration.sms.appChannelOptions.toggleAppActivated,
        this.campaign.shotConfiguration.email.appChannelOptions.toggleAppActivated,
        this.campaign.shotConfiguration.push.appChannelOptions.toggleAppActivated,
        this.campaign.shotConfiguration.whatsapp.appChannelOptions.toggleAppActivated,
      ].find((channel) => channel)
    },

    async validatePreviewInfos() {
      this.errorShotQuantity = false
      this.defaultMsg = false
      await this.validateInfos()
    },

    async saveCampaign() {
      this.errorShotQuantity = true
      this.defaultMsg = true
      const isValid = await this.validateInfos()
      this.defaultMsg = false
      if (!isValid) return
      if (this.campaign.shotConfiguration?.push?.appChannelOptions?.toggleAppActivated) {
        this.campaign.shotConfiguration.push.action = this.getAction
      }
      this.SET_MARIA_CAMPAIGN(this.campaign)
      this.forcePush = true
      this.$router.push({ name: 'Revision' })
    },

    async goBack() {
      const allChannelsClosed = Object.entries(this.campaign.shotConfiguration).every((channel) => !channel[1].appChannelOptions.toggleAppActivated)
      let isValid = false
      if (!allChannelsClosed) {
        if (!this.hasPrice() && this.hasSubvariable()) {
          isValid = true
        } else {
          isValid = await this.validateInfos()
        }
        if (!isValid) return
      }
      this.forcePush = true
      if (this.campaign.shotConfiguration?.push?.appChannelOptions?.toggleAppActivated) {
        this.campaign.shotConfiguration.push.action = this.getAction
      }
      this.SET_MARIA_CAMPAIGN({ ...this.campaign, ...this.mariaCampaign })
      this.$router.push({ name: 'Budget' })
    },

    async saveDraft() {
      this.loadingSaveDraft = true
      if (this.campaign.shotConfiguration?.push?.appChannelOptions?.toggleAppActivated) {
        this.campaign.shotConfiguration.push.action = this.getAction
        if (this.selectedEmailType === 'BUILDER') await this.$refs.emailChannel.loadCampaignEmailConfiguration()
      }
      await this.upsertDraftMariaCampaign(
        {
          ...this.campaign,
          offer_sales: this.formatOfferSales(this.campaign.offer_sales),
          percentage_offer_sales: this.formatOfferSalesPercentage(this.campaign.percentage_offer_sales),
          takepay_offer_sales: this.formatOfferSalesTakeAndPay(this.campaign.takepay_offer_sales),
          combo_offer_sales: this.formatOfferSalesCombo(this.campaign.combo_offer_sales),
          ...this.getPartialPayload(),
        },
        this.isCampaignActivated
      )
      this.loadingSaveDraft = false
    },
  },

  validations() {
    if (this.savingDraft) {
      return {
        campaign: {
          title: {
            required,
            minLength: minLength(2),
            notOnlySpace,
          },
          description: {
            notOnlySpace,
          },
        },
      }
    }
    return {
      ...(this.tab === 'external'
        ? {
            redirectExternal: {
              required,
              notOnlySpace,
              isUrl,
            },
          }
        : {}),
      campaign: {
        ...(this.campaign.shotConfiguration.whatsapp.appChannelOptions.toggleAppActivated
          ? {
              senders: {
                required,
                minLength: minLength(1),
              },
            }
          : {}),
        shotConfiguration: {
          ...(this.campaign.shotConfiguration.sms.appChannelOptions.toggleAppActivated
            ? {
                sms: {
                  text: { required, notOnlySpace },
                  credits: { required, isPositive },
                },
              }
            : {}),
          ...(this.campaign.shotConfiguration.email.appChannelOptions.toggleAppActivated
            ? {
                email: this.errorShotQuantity
                  ? {
                      credits: { required, isPositive },
                      message: {
                        subject: {
                          required,
                          notOnlySpace,
                        },
                        processed_html: {
                          required,
                          notOnlySpace,
                        },
                        ...(this.selectedEmailType === 'SIMPLE'
                          ? {
                              simple_email_configuration: {
                                subject: {
                                  required,
                                  maxLength: maxLength(this.maxSubjectLength),
                                  notOnlySpace,
                                },
                                text: {
                                  required,
                                  maxLength: maxLength(this.maxTextLength),
                                  notOnlySpace,
                                },
                                click_to_call_button_text: {
                                  required,
                                  maxLength: maxLength(this.maxButtonTextLength),
                                  notOnlySpace,
                                },
                                click_to_call_button_link: {
                                  required,
                                  notOnlySpace,
                                  isUrl,
                                },
                                footer: {
                                  required,
                                  maxLength: maxLength(this.maxFooterLength),
                                  notOnlySpace,
                                },
                                image: {
                                  required,
                                },
                                header_image: {
                                  required,
                                },
                              },
                            }
                          : {}),
                        ...(this.selectedEmailType === 'HTML'
                          ? {
                              html_email_configuration: {
                                url: {
                                  notOnlySpace,
                                  isUrl,
                                },
                              },
                            }
                          : {}),
                        ...(this.selectedEmailType === 'BUILDER'
                          ? {
                              builder_email_configuration: {
                                body: {
                                  required,
                                },
                                counters: {
                                  required,
                                },
                              },
                            }
                          : {}),
                      },
                    }
                  : {
                      message: {
                        subject: {
                          required,
                          notOnlySpace,
                        },
                        processed_html: {
                          required,
                          notOnlySpace,
                        },
                        ...(this.selectedEmailType === 'SIMPLE'
                          ? {
                              simple_email_configuration: {
                                subject: {
                                  required,
                                  maxLength: maxLength(this.maxSubjectLength),
                                  notOnlySpace,
                                },
                                text: {
                                  required,
                                  maxLength: maxLength(this.maxTextLength),
                                  notOnlySpace,
                                },
                                click_to_call_button_text: {
                                  required,
                                  maxLength: maxLength(this.maxButtonTextLength),
                                  notOnlySpace,
                                },
                                click_to_call_button_link: {
                                  required,
                                  notOnlySpace,
                                  isUrl,
                                },
                                footer: {
                                  required,
                                  maxLength: maxLength(this.maxFooterLength),
                                  notOnlySpace,
                                },
                                image: {
                                  required,
                                },
                                header_image: {
                                  required,
                                },
                              },
                            }
                          : {}),
                        ...(this.selectedEmailType === 'HTML'
                          ? {
                              html_email_configuration: {
                                url: {
                                  notOnlySpace,
                                  isUrl,
                                },
                              },
                            }
                          : {}),
                        ...(this.selectedEmailType === 'BUILDER'
                          ? {
                              builder_email_configuration: {
                                body: {
                                  required,
                                },
                                counters: {
                                  required,
                                },
                              },
                            }
                          : {}),
                      },
                    },
              }
            : {}),
          ...(this.campaign.shotConfiguration.push.appChannelOptions.toggleAppActivated
            ? {
                push: {
                  title: {
                    required,
                    maxLength: maxLength(this.maxPushTitleLength),
                    notOnlySpace,
                  },
                  ...(this.showImage
                    ? {
                        image_url: {
                          required,
                          notOnlySpace,
                        },
                      }
                    : {}),
                  credits: { required, isPositive },
                  ...(this.campaign.shotConfiguration.push.innerMessage
                    ? {
                        inner_message_title: {
                          required,
                          maxLength: maxLength(this.maxPushInnerTitleLength),
                          notOnlySpace,
                        },
                      }
                    : {}),
                },
              }
            : {}),
          ...(this.campaign.shotConfiguration.whatsapp.appChannelOptions.toggleAppActivated
            ? {
                whatsapp: {
                  credits: { required, isPositive },
                  message: {
                    text: {
                      required: requiredIf(function () {
                        const hasDocumentSelected = Boolean(this.campaign?.shotConfiguration?.whatsapp?.message?.attachment?.mimetype)

                        return !hasDocumentSelected
                      }),
                      maxLength: maxLength(this.maxWhatsappLength),
                      notOnlySpace,
                    },
                  },
                },
              }
            : {}),
        },
      },
    }
  },
}
</script>

<style lang="scss" scoped>
::v-deep .configurar-push > .v-card {
  box-shadow: none;
  padding: 0 4px !important;
}

.page-subtitle {
  font-weight: bold;
  font-size: 26px;
  line-height: 120%;
  letter-spacing: 0.01em;
  color: $cinza-d10;
}

.page-description {
  font-size: 16px;
  line-height: 120%;
  letter-spacing: 0.4px;
  color: $cinza-0;
}
</style>

<style lang="scss">
$primary-color: $cinza-l70;

.credits-container {
  background: $cinza-l90;
  height: 56px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  padding-left: 12px;
  & p {
    margin: 0;
    font-size: 16px;
    color: $cinza-d10;
  }
}

.email-marketing {
  .hint-text {
    font-size: 12px;
    letter-spacing: 0.23px;
    color: $cinza-d10;
  }

  .email-marketing-card-title {
    margin-bottom: 8px;
    font-size: 18px;
    font-weight: bold;
    letter-spacing: 0.33px;
    color: $cinza-d20;
  }

  .label-box {
    min-height: 24px;

    span {
      display: block;
      line-height: 2 !important;
      margin-bottom: 0;
      text-align: right;
      opacity: 0.8;
      font-size: 11px;
      font-weight: 500;
      line-height: 1.18;
      letter-spacing: 0.18px;
    }
  }

  .background-img-header {
    background-color: $primary-color;
    height: 200px;
  }

  .background-img-content {
    background-color: $primary-color;
    height: 350px;
  }

  .img-text {
    height: 85px;
  }

  .email-footer {
    background-color: $cinza-l90;
    text-align: center;

    span {
      font-size: 12px;
    }
  }
}
.v-application--is-ltr .v-textarea.v-text-field--enclosed .v-text-field__slot textarea {
  margin-right: 2px;
  margin-top: 5px !important;
}
.v-application--is-ltr .v-textarea.v-text-field--enclosed .v-text-field__slot {
  margin-bottom: 5px;
}
</style>
